import React, { useEffect, useState, useRef } from 'react';
import { styled } from '@compiled/react';
import isEmpty from 'lodash/isEmpty';
import Button from '@atlaskit/button';
import { Box, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import {
	useAnalyticsEvents,
	fireUIAnalytics,
	FireScreenAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { useAIAnalyticsContext } from '@atlassian/jira-instrumentation-common/src/ai-analytics-context/context.tsx';
import { AI_EVENTS } from '@atlassian/jira-instrumentation-common/src/ai-analytics-context/common.tsx';
import {
	DEFAULT_SIMILAR_INCIDENTS_CARD,
	MAX_SIMILAR_INCIDENTS_CARD,
} from '../../common/constants.tsx';
import type { TProps, TSimilarIncident } from '../../common/types.tsx';
import { useRelatedIncidentsForIssue } from '../../services/context.tsx';
import SimilarIncidentsFeedback from './feedback/index.tsx';
import SimilarIncidentsPanelHeader from './header/index.tsx';
import messages from './messages.tsx';
import SimilarIncidentsCard from './similar-incident-card/index.tsx';
import SkeletonLoadingContainer from './skeleton/index.tsx';

type AIEventsFired = Record<'init', boolean>;

export const SimilarIncidentsPanelView = ({ sessionId, defaultExpanded }: TProps) => {
	const [isExpanded, setExpanded] = useState(Boolean(defaultExpanded));
	const [showMore, setShowMore] = useState(false);
	const { fireTrackAI, addTraceId } = useAIAnalyticsContext();
	const aiEventsFiredRef = useRef<AIEventsFired>({ init: false });

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const [{ similarIncidentData, traceIds, error, isLoading }, { fetchRelatedIncidentsForIssue }] =
		useRelatedIncidentsForIssue();

	useEffect(() => {
		async function fetchData() {
			await fetchRelatedIncidentsForIssue(createAnalyticsEvent);
		}
		if (isExpanded) {
			if (!aiEventsFiredRef.current.init) {
				fireTrackAI(AI_EVENTS.INIT);
				aiEventsFiredRef.current.init = true;
			}
			fetchData();
		}
	}, [fetchRelatedIncidentsForIssue, createAnalyticsEvent, isExpanded, fireTrackAI]);

	useEffect(() => {
		traceIds.forEach(addTraceId);
	}, [addTraceId, traceIds]);

	useEffect(() => {
		if (isExpanded && !isEmpty(similarIncidentData?.similarIssues)) {
			fireTrackAI(AI_EVENTS.VIEWED, {
				relatedIssuesCount: similarIncidentData?.similarIssues?.length ?? 0,
			});
		}
	}, [isExpanded, fireTrackAI, similarIncidentData?.similarIssues]);

	useEffect(() => {
		if (!isExpanded && isLoading) {
			fireTrackAI(AI_EVENTS.DISMISSED);
		}
	}, [fireTrackAI, isExpanded, isLoading]);

	useEffect(() => {
		if (error) {
			fireTrackAI(AI_EVENTS.ERROR, {
				aiErrorCode: error.statusCode,
				aiErrorMessage: error.message,
			});
		}
	}, [error, fireTrackAI]);

	const handleShowMore = () => {
		setShowMore(true);
		fireUIAnalytics(createAnalyticsEvent({}), 'showMoreButton clicked', {
			suggestionId: similarIncidentData.suggestionId,
		});
		fireTrackAI(AI_EVENTS.ACTIONED, {
			aiResultAction: 'showMore',
		});
	};

	const renderSimilarIncidentsBody = () => (
		<>
			{similarIncidentData && similarIncidentData.similarIssues.length ? (
				<FieldsContainer data-testid="smart-related-issues-panel.ui.similar-incidents.similar-incidents-container">
					<FireScreenAnalytics />
					{similarIncidentData.similarIssues
						.slice(0, DEFAULT_SIMILAR_INCIDENTS_CARD)
						.map((curr: TSimilarIncident, index: number) => (
							<SimilarIncidentsCard
								suggestionId={similarIncidentData.suggestionId}
								currentIncident={curr}
								rank={index}
								key={index}
							/>
						))}
					{showMore &&
						similarIncidentData.similarIssues
							.slice(DEFAULT_SIMILAR_INCIDENTS_CARD, MAX_SIMILAR_INCIDENTS_CARD)
							.map((curr: TSimilarIncident, index: number) => (
								<SimilarIncidentsCard
									suggestionId={similarIncidentData.suggestionId}
									currentIncident={curr}
									rank={index}
									key={index}
								/>
							))}
					{!showMore && similarIncidentData.similarIssues.length > 3 && (
						<Button
							appearance="link"
							onClick={handleShowMore}
							interactionName="auto-generated.jira-smart-related-issues-panel.ui.similar-incidents.button"
						>
							{formatMessage(messages.showMoreButton)}
						</Button>
					)}
					<SimilarIncidentsFeedback
						sessionId={sessionId}
						suggestionId={similarIncidentData.suggestionId}
					/>
				</FieldsContainer>
			) : (
				<FieldsContainer data-testid="smart-related-issues-panel.ui.similar-incidents.similar-incidents-container">
					<Text as="p">
						{formatMessage(
							fg('jira-issue-terminology-refresh-m3')
								? messages.noSimilarIncidentsIssueTermRefresh
								: messages.noSimilarIncidents,
						)}
					</Text>
				</FieldsContainer>
			)}
		</>
	);

	const renderSimilarIncidents = () => (
		<>
			<SimilarIncidentsPanelHeader
				sessionId={sessionId}
				onExpand={() => setExpanded(true)}
				onCollapse={() => {
					setShowMore(false);
					setExpanded(false);
				}}
				isExpanded={isExpanded}
			/>
			{isExpanded && isLoading && <SkeletonLoadingContainer />}
			{isExpanded && !isLoading && <Box xcss={panelBodyStyle}>{renderSimilarIncidentsBody()}</Box>}
		</>
	);

	return <SimilarIncidentsContainer>{renderSimilarIncidents()}</SimilarIncidentsContainer>;
};

export default SimilarIncidentsPanelView;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SimilarIncidentsContainer = styled.div<{
	hasContentItems?: boolean;
}>({
	display: 'flex',
	flexDirection: 'column',
});

const panelBodyStyle = xcss({
	marginBottom: 'space.300',
	display: 'flex',
	flexDirection: 'column',
	borderBottomLeftRadius: 'border.radius.100',
	borderBottomRightRadius: 'border.radius.100',
	borderBottom: `1px solid ${token('color.border')}`,
	borderLeft: `1px solid ${token('color.border')}`,
	borderRight: `1px solid ${token('color.border')}`,
	backgroundColor: 'elevation.surface',
	paddingLeft: 'space.100',
	paddingRight: 'space.100',
	paddingBottom: 'space.100',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FieldsContainer = styled.div({
	overflow: 'hidden',
});
